import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiMessageCircle, FiSend, FiX, FiExternalLink } from 'react-icons/fi';
import ApiURL from '../Api/Api';

const assetUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${ApiURL.replace(/\/$/, '')}/${String(path).replace(/^\/+/, '')}`;
};

const timeLabel = () =>
  new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const CompanyOwnerChatbox = () => {
  const role = localStorage.getItem('role');
  const userId = localStorage.getItem('id');
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [quickOptions, setQuickOptions] = useState([]);
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [activeIncident, setActiveIncident] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [raisingIncidentId, setRaisingIncidentId] = useState(0);
  const [loadError, setLoadError] = useState('');
  const bottomRef = useRef(null);

  const avatar = useMemo(() => assetUrl(settings?.avatar_path), [settings]);
  const isAvailable = role === 'company_owner' && userId;

  useEffect(() => {
    if (!isAvailable) return;

    const loadChatbox = async () => {
      try {
        const response = await fetch(`${ApiURL}/getCompanyChatbox.php?user_id=${encodeURIComponent(userId)}`);
        const data = await response.json();
        if (data.status !== 'success') {
          throw new Error(data.message || 'Chat help unavailable');
        }
        setSettings(data.settings || {});
        setQuickOptions(data.quick_options || []);
        setIncidentTypes(data.incident_types || []);
        setActiveIncident(data.open_incident || null);
        setMessages([
          {
            sender: 'bot',
            text: data.open_incident
              ? `Incident in progress: ${data.open_incident.incident_type}. Keep sharing updates here and the TxnAlgo2Bot support team will review the full conversation.`
              : data.settings?.greeting || 'Hi! How can I help you?',
            time: timeLabel(),
          },
        ]);
      } catch (error) {
        setLoadError(error.message || 'Chat help unavailable');
      }
    };

    loadChatbox();
  }, [isAvailable, userId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  if (!isAvailable || loadError || settings?.status === 'inactive') {
    return null;
  }

  const sendMessage = async ({ text, faqId = 0 }) => {
    const cleanText = String(text || '').trim();
    if (!cleanText && !faqId) return;

    setMessages((prev) => [...prev, { sender: 'owner', text: cleanText, time: timeLabel() }]);
    setInput('');
    setIsSending(true);

    try {
      const response = await fetch(`${ApiURL}/companyOwnerChatAsk.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, message: cleanText, faq_id: faqId }),
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Could not answer');
      }
      setMessages((prev) => [
        ...prev,
        {
          sender: 'bot',
          text: data.answer,
          time: timeLabel(),
          matchType: data.match_type,
          reportId: data.report_id,
          whatsappNumber: data.whatsapp_number,
          whatsappLabel: data.whatsapp_label,
          incidentId: data.incident_id,
          incidentStatus: data.incident_status,
        },
      ]);
      if (data.incident_id) {
        setActiveIncident((prev) => prev || {
        id: data.incident_id,
        report_id: data.report_id,
          incident_type: 'Incident in progress',
          status: data.incident_status || 'open',
        });
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          sender: 'bot',
          text: error.message || settings?.fallback_answer || 'Please contact support.',
          time: timeLabel(),
          matchType: 'error',
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const whatsappHref = settings?.whatsapp_number
    ? `https://wa.me/${String(settings.whatsapp_number).replace(/\D/g, '')}`
    : '';

  const hrefForWhatsapp = (number) => {
    const clean = String(number || settings?.whatsapp_number || '').replace(/\D/g, '');
    return clean ? `https://wa.me/${clean}` : '';
  };

  const raiseIncident = async (message, incidentType) => {
    if (!message?.reportId) return;
    setRaisingIncidentId(message.reportId);
    try {
      const response = await fetch(`${ApiURL}/chatboxRaiseIncident.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          report_id: message.reportId,
          message: message.text,
          incident_type_id: incidentType?.id || 0,
          incident_type: incidentType?.title || '',
        }),
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Could not raise incident');
      }
      setMessages((prev) => prev.map((item) => (
        item.reportId === message.reportId ? { ...item, incidentRaised: true, matchType: 'incident' } : item
      )));
      setActiveIncident({
        id: data.incident_id || data.incident?.id,
        report_id: data.report_id || message.reportId,
        incident_type: data.incident_type || incidentType?.title || data.incident?.incident_type || 'Incident',
        status: 'open',
      });
      setMessages((prev) => [...prev, {
        sender: 'bot',
        text: `Incident raised: ${data.incident_type || incidentType?.title || 'Incident'}. Share any extra details here. Our support team will connect with you.`,
        time: timeLabel(),
        matchType: 'incident',
      }]);
    } catch (error) {
      setMessages((prev) => [...prev, {
        sender: 'bot',
        text: error.message || 'Incident could not be raised right now.',
        time: timeLabel(),
        matchType: 'error',
      }]);
    } finally {
      setRaisingIncidentId(0);
    }
  };

  const closeIncident = async () => {
    if (!activeIncident?.id) return;
    try {
      const response = await fetch(`${ApiURL}/chatboxCloseIncident.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, incident_id: activeIncident.id }),
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Could not close incident');
      }
      setActiveIncident(null);
      setMessages((prev) => [...prev, {
        sender: 'bot',
        text: 'Marked as fixed. You can start a fresh chat or raise a new incident anytime.',
        time: timeLabel(),
      }]);
    } catch (error) {
      setMessages((prev) => [...prev, {
        sender: 'bot',
        text: error.message || 'Incident could not be closed right now.',
        time: timeLabel(),
        matchType: 'error',
      }]);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-[70] font-sans">
      {isOpen && (
        <div className="mb-4 w-[calc(100vw-2.5rem)] max-w-[390px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between bg-emerald-950 px-5 py-4 text-white">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 overflow-hidden rounded-full border-2 border-white/80 bg-emerald-800">
                {avatar ? (
                  <img src={avatar} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-sm font-bold">TXN</div>
                )}
              </div>
              <div>
                <div className="text-lg font-semibold">{settings?.title || 'Help Center'}</div>
                <div className="flex items-center gap-2 text-xs text-emerald-100">
                  <span className="h-2 w-2 rounded-full bg-emerald-400" />
                  Usually replies in a minute
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded-full border border-white/25 p-2 text-white transition hover:bg-white/10"
              aria-label="Close chat"
            >
              <FiX />
            </button>
          </div>

          <div className="h-80 space-y-4 overflow-y-auto bg-slate-50 px-4 py-4">
            {activeIncident && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-left">
                <div className="text-xs font-bold uppercase tracking-[0.16em] text-rose-700">Incident In Progress</div>
                <div className="mt-1 text-sm font-semibold text-rose-950">{activeIncident.incident_type}</div>
              </div>
            )}
            {messages.map((message, index) => (
              <div key={`${message.sender}-${index}`} className={message.sender === 'owner' ? 'text-right' : 'text-left'}>
                <div className={`inline-block max-w-[82%] ${message.sender === 'owner' ? '' : 'text-left'}`}>
                  <div
                    className={`rounded-2xl px-4 py-3 text-sm leading-6 shadow-sm ${
                      message.sender === 'owner'
                        ? 'bg-emerald-700 text-white'
                        : 'bg-white text-slate-800 border border-slate-100'
                    }`}
                  >
                    {message.text}
                  </div>
                  {message.sender === 'bot' && ['fallback', 'error'].includes(message.matchType) && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {hrefForWhatsapp(message.whatsappNumber) && (
                        <a
                          href={hrefForWhatsapp(message.whatsappNumber)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-full bg-lime-700 px-4 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-lime-800"
                        >
                          {message.whatsappLabel || settings?.whatsapp_label || 'Talk on WhatsApp'} <FiExternalLink />
                        </a>
                      )}
                      {message.reportId && !activeIncident && (
                        <div className="w-full space-y-1.5">
                          <div className="text-[11px] font-bold text-slate-500">Raise incident as</div>
                          {(incidentTypes.length ? incidentTypes : [{ id: 0, title: 'Any other incident' }]).map((type) => (
                            <button
                              key={type.id || type.title}
                              type="button"
                              onClick={() => raiseIncident(message, type)}
                              disabled={message.incidentRaised || raisingIncidentId === message.reportId}
                              className="mr-1.5 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {raisingIncidentId === message.reportId ? 'Raising...' : type.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="mt-1 text-[11px] text-slate-400">{message.time}</div>
              </div>
            ))}
            {isSending && <div className="text-xs text-slate-400">Typing...</div>}
            <div ref={bottomRef} />
          </div>

          {quickOptions.length > 0 && (
            <div className="border-t border-slate-100 bg-white px-4 py-3">
              <div className="grid gap-2">
                {quickOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => sendMessage({ text: option.question, faqId: option.id })}
                    className="rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-left text-xs font-semibold text-emerald-900 transition hover:border-emerald-300 hover:bg-emerald-100"
                  >
                    {option.question}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-slate-100 bg-white p-4">
            {activeIncident && (
              <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                <span className="text-xs font-bold text-emerald-900">Issue solved?</span>
                <button
                  type="button"
                  onClick={closeIncident}
                  className="rounded-full bg-emerald-700 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-800"
                >
                  Mark Fixed
                </button>
              </div>
            )}
            <form
              onSubmit={(event) => {
                event.preventDefault();
                sendMessage({ text: input });
              }}
              className="flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder={settings?.placeholder || 'Need Help? Ask us anything'}
                className="min-w-0 flex-1 rounded-full border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              />
              <button
                type="submit"
                disabled={isSending || !input.trim()}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-700 text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                aria-label="Send"
              >
                <FiSend />
              </button>
            </form>
            {whatsappHref && (
              <a
                href={whatsappHref}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-emerald-800 hover:text-emerald-950"
              >
                {settings?.whatsapp_label || 'Talk on WhatsApp'} <FiExternalLink />
              </a>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-800 text-2xl text-white shadow-2xl ring-4 ring-white transition hover:-translate-y-0.5 hover:bg-emerald-900"
        aria-label="Open help chat"
      >
        {avatar ? <img src={avatar} alt="" className="h-full w-full rounded-full object-cover" /> : <FiMessageCircle />}
      </button>
    </div>
  );
};

export default CompanyOwnerChatbox;
